import { useEffect } from "react";
import ATCField from "@features/vessel-call/vessel-call-status/PortServices/flow/extra-fields/ATCField";
import ATSField from "@features/vessel-call/vessel-call-status/PortServices/flow/extra-fields/ATSField";
import Attachment from "@features/vessel-call/vessel-call-status/PortServices/flow/extra-fields/Attachment";
import ChargedPartyField from "@features/vessel-call/vessel-call-status/PortServices/flow/extra-fields/ChargedPartyField";
import ETCField from "@features/vessel-call/vessel-call-status/PortServices/flow/extra-fields/ETCField";
import ExternalNotesAndAttachmentsComponent from "@features/vessel-call/vessel-call-status/PortServices/flow/extra-fields/ExternalNotesAndAttachments";
import InternalNotesAndAttachmentsComponent from "@features/vessel-call/vessel-call-status/PortServices/flow/extra-fields/InternalNotesAndAttachments";
import NotesField from "@features/vessel-call/vessel-call-status/PortServices/flow/extra-fields/NotesField";
import RejectionReasonAndAttachment from "@features/vessel-call/vessel-call-status/PortServices/flow/extra-fields/RejectionReasonAndAttachment";
import type { ChangeStatusDrawerInfo } from "@features/vessel-call/vessel-call-status/PortServices/hoc/withDrawers";
import { usePortServicesFlow } from "../context/PortServicesFlowContext";
import ShipChandlingLifeboatsCloseFields from "./ShipChandlingLifeboatsCloseFields";

interface ExtraFieldProps {
  serviceDetails: ChangeStatusDrawerInfo;
}

const extraFieldsMap = {
  chargedParty: (props: ExtraFieldProps) => (
    <ChargedPartyField initialValue={props.serviceDetails.chargedParty} />
  ),
  notes: () => <NotesField />,
  attachment: () => <Attachment isPdfonly={false} />,
  internalNotes: () => <InternalNotesAndAttachmentsComponent />,
  externalNotes: () => <ExternalNotesAndAttachmentsComponent />,
  rejectionReason: () => <RejectionReasonAndAttachment />,
  ats: (props: ExtraFieldProps) => <ATSField {...props} />,
  etc: (props: ExtraFieldProps) => <ETCField {...props} />,
  atc: (props: ExtraFieldProps) => <ATCField {...props} />,
};

const parsePositiveInteger = (value?: string | number) => {
  if (value == null || value === "") {
    return undefined;
  }

  const parsed = Number(value);

  if (!Number.isInteger(parsed) || parsed <= 0) {
    return undefined;
  }

  return parsed;
};
const LifeboatsClosedFieldsInitializer = ({ serviceDetails }: ExtraFieldProps) => {
  const { setNumberOfPackagesHandled, setSpares, setIncidents, setResources } =
    usePortServicesFlow();

  useEffect(() => {
    setNumberOfPackagesHandled(parsePositiveInteger(serviceDetails.numberOfPackagesHandled));
    setSpares(parsePositiveInteger(serviceDetails.spares));
    setIncidents(serviceDetails.incidents ?? "");
    setResources([]);
  }, [serviceDetails, setIncidents, setNumberOfPackagesHandled, setResources, setSpares]);

  return null;
};

type ShipChandlingLifeboatsExtraFieldsProps = {
  currentFlowStep: any;
  selectedStatus: string;
} & ExtraFieldProps;

const ShipChandlingLifeboatsExtraFields = ({
  currentFlowStep,
  selectedStatus,
  ...restProps
}: ShipChandlingLifeboatsExtraFieldsProps) => {
  const isClosedStatus = selectedStatus === "CLOSED";

  if (isClosedStatus) {
    return (
      <>
        <LifeboatsClosedFieldsInitializer {...restProps} />
        <ShipChandlingLifeboatsCloseFields
          currentFlowStep={currentFlowStep}
          selectedStatus={selectedStatus}
          {...restProps}
        />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {currentFlowStep?.statusesList?.[selectedStatus]?.extraFields?.length
        ? currentFlowStep.statusesList[selectedStatus].extraFields.map(
            (field: keyof typeof extraFieldsMap) => {
              const FieldComponent = extraFieldsMap[field];

              if (!FieldComponent) return null;

              return (
                <div className="mt-4" key={field}>
                  <FieldComponent {...restProps} />
                </div>
              );
            },
          )
        : null}
    </div>
  );
};

export default ShipChandlingLifeboatsExtraFields;
